"use client";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

import { SupportedLanguage } from "@/lib/shared";

import Link from "next/link";

import lang from "./lang.json";
import sidebarLang from "@/app/[locale]/(main)/lang.json";

import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";
import {
  sidebarItems,
  isAlreadyOnRoute,
  isSidebarItemActive,
} from "../MainSidebar";
import { usePathname, useRouter } from "next/navigation";
import { useState, useMemo, useEffect } from "react";
import { localizedPath, pathWithoutLocale } from "@/lib/localePath";
import LinkPendingNotifier from "@/components/customs/navigation-pending/LinkPendingNotifier";
import { useNavigationPending } from "@/components/customs/navigation-pending/NavigationPendingContext";
import { cn } from "@/lib/utils";
import SelectLanguage from "./SelectLanguage";

type SheetMainMenuProps = {
  locale: SupportedLanguage;
};

export default function SheetMainMenu({ locale }: SheetMainMenuProps) {
  const router = useRouter();
  const pathname = usePathname();
  const pathAfterLocale = useMemo(
    () => pathWithoutLocale(pathname),
    [pathname],
  );
  const { navigationTarget, setPendingFromLink } = useNavigationPending();
  const effectivePath = navigationTarget ?? pathAfterLocale;

  const [isOpen, isOpenSet] = useState(false);

  const mobilePrefetchTargets = useMemo(
    () => sidebarItems("/").map((item) => localizedPath(locale, item.href)),
    [locale],
  );

  useEffect(() => {
    mobilePrefetchTargets.forEach((href) => {
      try {
        router.prefetch(href);
      } catch {
        /* ignore */
      }
    });
  }, [router, mobilePrefetchTargets]);

  return (
    <Sheet onOpenChange={isOpenSet} open={isOpen}>
      <SheetTrigger asChild>
        <Button variant="link" size="sm">
          <Menu />
        </Button>
      </SheetTrigger>
      <SheetContent
        side={locale === "ar" ? "right" : "left"}
        className="min-h-0 gap-0 overflow-hidden"
      >
        <SheetHeader>
          <SheetTitle>
            {lang["main-menu-title"][locale] || "Main Menu"}
          </SheetTitle>
          <SheetDescription className="sr-only">
            {lang["sheet-main-menu-desc"][locale]}
          </SheetDescription>
        </SheetHeader>

        <hr />

        <div className="flex h-full min-h-0 flex-1 flex-col">
          <div className="flex min-h-0 flex-1 flex-col gap-y-4 overflow-y-auto px-4 pb-4">
            {sidebarItems(effectivePath).map((item) => {
              const active = isSidebarItemActive(effectivePath, item.href);
              return (
                <Link
                  onClick={() => isOpenSet(false)}
                  key={item.href}
                  href={localizedPath(locale, item.href)}
                  prefetch
                  onPointerDown={(e) => {
                    if (e.button !== 0) return;
                    if (isAlreadyOnRoute(pathAfterLocale, item.href)) return;
                    setPendingFromLink(item.href, true);
                  }}
                  onPointerEnter={() => {
                    try {
                      router.prefetch(localizedPath(locale, item.href));
                    } catch {
                      /* ignore */
                    }
                  }}
                  className={cn(
                    "flex cursor-pointer items-center gap-3 transition-colors duration-0",
                    active ? "font-bold text-primary" : "text-gray-600",
                  )}
                >
                  <LinkPendingNotifier pathAfterLocale={item.href} />
                  <span className="font-medium">
                    {sidebarLang[item.titleKey][locale]}
                  </span>
                </Link>
              );
            })}
          </div>

          <div className="mt-auto border-t px-4 pb-8 pt-4">
            <span className="mb-3 block text-sm font-medium text-muted-foreground">
              {lang["sheet-language-label"][locale]}
            </span>
            <div className="flex w-full justify-start [&_button]:h-10 [&_button]:min-h-10 [&_button]:px-2">
              <SelectLanguage />
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
